import { LinesField, TextField } from "@/app/fields";
import { CHANNEL_KIND, type Channel, type ChannelKind, type Supplier } from "@/app/master";
import { idOptions } from "@/app/queries";
import type { Errors } from "@/app/useDraft";
import { Alert, Checkbox, Field, Fieldset, Form, Select, Switch } from "@/ui";
import { ChannelCallback } from "./ChannelCallback";
import { TelegramBaseURL, TelegramFields } from "./TelegramFields";
import { CAPABILITIES, CIRCUIT_FIELDS, type CapabilityKey, NPI_OPTIONS, TON_OPTIONS, type ChannelDraft, type ChannelErrors, type CompatDraft, type SMPPDraft, type TLSDraft } from "./channelDraft";

interface Props {
  draft: ChannelDraft; errors: Errors<ChannelDraft>; set: <K extends keyof ChannelDraft>(k: K, v: ChannelDraft[K]) => void;
  /** 编辑的那一行，新建时不给；回调的重新生成按它整行回存。 */
  row?: Channel; canWrite?: boolean; suppliers?: Supplier[];
}

/** 新建时能选的通道类型；Telegram 即 protocol=http、adapter=telegram（D207）。 */
const NEW_KINDS: ChannelKind[] = ["smpp", "telegram"];

/** 能力标记的补充说明：Telegram 的回执锁定为支持（D207 第 6 条）；其余标记照常参与路由（架构说明 11.4），按原文判断，
 * 只写对路由的影响，不推断 Telegram 自身的行为。有效期、Unicode 与最大长度是硬过滤（internal/worker/routing 的 reject），
 * 可信、幂等键与自定义 sender 只交给路由策略；sender 的硬过滤看“支持的 sender”列表，不看这个标记。 */
const TELEGRAM_CAPABILITY_NOTE: Record<CapabilityKey, string> = {
  dlr_supported: "Telegram 通道必须支持回执：成本只按送达计，TTL 超时也靠回执判定",
  dlr_trusted: "不参与路由硬过滤，只作为路由策略的输入",
  validity_period_supported: "关掉后带有效期的消息路由时会剔除这条通道",
  idempotency_key_supported: "不参与路由硬过滤，只作为路由策略的输入",
  custom_sender_supported: "Telegram 不看 sender；这个标记不参与路由硬过滤，sender 过滤看下方“支持的 sender”列表",
  unicode_supported: "按原文编码判断：关掉后需要 UCS-2 的消息路由时会剔除这条通道",
};

/** Telegram 通道最大长度的说明：路由按原文长度过滤，不看取出的验证码。 */
const TELEGRAM_MAX_LENGTH_NOTE = "按原文长度判断，超过的消息路由时会剔除这条通道";

/** 通道表单的字段，见 docs/03 3.5。连接栏逐项填写（#184）：SMPP 协议参数、最大 bind 数（决策 D86）、TLS，保存后 connector 只重连这条通道（D153）；SMPP 通道另有"高级"栏的对接兼容参数（D151）；
 * Telegram 通道填 Gateway 参数，另有回执回调栏（D207、D209）；适配器认不出的旧 HTTP 通道只显示提示（#187、#405）。不带币种（决策 D85）。 */
export function ChannelForm({ draft, errors: fieldErrors, set, row, canWrite = true, suppliers }: Props) {
  const errors = fieldErrors as ChannelErrors;
  const isNew = !row;
  const telegram = draft.kind === "telegram";
  return (
    <div className="ax-stack">
      <Fieldset title="基本">
        <Form columns={2}>
          <Field label="供应商" required error={errors.supplier_id}>
            <Select block options={idOptions(suppliers?.filter((s) => !s.system))} value={draft.supplier_id || null} onChange={(v) => set("supplier_id", v)} placeholder="选择供应商" invalid={!!errors.supplier_id} />
          </Field>
          <TextField id="ch-name" label="名称" required value={draft.name} error={errors.name} onChange={(v) => set("name", v)} />
          <Field label="协议" required hint={isNew ? "保存后不能修改" : undefined}
            help="Telegram 经 Telegram Gateway 只发验证码：发送时从原文取出 4–8 位数字，原文不发出，按条计费">
            <Select block ariaLabel="协议" options={(isNew ? NEW_KINDS : [draft.kind]).map((k) => ({ value: k, label: CHANNEL_KIND[k] }))} value={draft.kind}
              onChange={(v) => set("kind", v as ChannelKind)} disabled={!isNew} />
          </Field>
          <TextField id="ch-route-type" label="路由类型" value={draft.route_type} hint="例如直连、二级，只做展示" onChange={(v) => set("route_type", v)} />
        </Form>
        {!isNew && <Switch label="启用" checked={draft.status === "active"} onChange={(on) => set("status", on ? "active" : "disabled")} />}
      </Fieldset>
      <Fieldset title="连接">
        {draft.kind === "smpp" ? (
          <>
            {!isNew && <Alert title="保存后只重连这条通道">connector 先暂停取消息、等在途提交返回，再按新参数重连，期间消息留在队列；最大 bind 数按差额增减，其他通道不受影响，不用重启。</Alert>}
            <Form>
              <SMPPFields value={draft.smpp} errors={errors} onChange={(v) => set("smpp", v)} />
              <TextField id="ch-max-binds" label="最大 bind 数" required type="number" mono value={draft.max_binds} error={errors.max_binds}
                hint="上游允许同时连接的数量；0 表示不连接" onChange={(v) => set("max_binds", v)} />
              <TLSFields value={draft.tls} errors={errors} onChange={(v) => set("tls", v)} />
            </Form>
          </>
        ) : telegram ? (
          <>
            {!isNew && <Alert title="保存后只重起这条通道">http-connector 按新参数重起这条通道的发送，其他通道不受影响，不用重启。</Alert>}
            <TelegramFields value={draft.telegram} errors={errors} isNew={isNew} onChange={(v) => set("telegram", v)} />
          </>
        ) : (
          <Alert tone="warning" title="不支持的 HTTP 适配器">
            目前只支持 Telegram 通道；这条 HTTP 通道只能修改通用字段，参数原样保留，启用会被拒绝。
          </Alert>
        )}
      </Fieldset>
      {draft.kind === "smpp" && (
        <Fieldset title="高级">
          <Alert title="保存后对新发送立即生效">缺省与原来的发送方式一致，只在上游有要求时修改。</Alert>
          <CompatFields value={draft.compat} errors={errors} onChange={(v) => set("compat", v)} />
        </Fieldset>
      )}
      {telegram && (
        <>
          <Fieldset title="回执回调"><ChannelCallback row={row} canWrite={canWrite} /></Fieldset>
          <Fieldset title="高级"><TelegramBaseURL value={draft.telegram} errors={errors} onChange={(v) => set("telegram", v)} /></Fieldset>
        </>
      )}
      <Fieldset title="熔断">
        <Switch label="启用熔断：提交失败率过高时自动避开这条通道，恢复后逐步放开" checked={draft.circuit_enabled} onChange={(on) => set("circuit_enabled", on)} />
        {draft.circuit_enabled && (
          <Form columns={2}>
            {CIRCUIT_FIELDS.map((f) => (
              <TextField key={f.key} id={`ch-circuit-${f.key}`} label={f.label} mono placeholder={`默认 ${f.fallback}`} value={draft.circuit[f.key]} error={errors[`circuit.${f.key}`]}
                hint={f.ratio ? "0 到 1 的小数；留空取默认" : "留空取默认"} onChange={(v) => set("circuit", { ...draft.circuit, [f.key]: v })} />
            ))}
          </Form>
        )}
      </Fieldset>
      <Fieldset title="限流与重试">
        <Form columns={2}>
          <TextField id="ch-tps" label="TPS" required type="number" mono value={draft.tps} error={errors.tps}
            hint={telegram ? "每个 http-connector 实例的限速" : undefined} help={telegram ? "多实例时合计放大到 TPS × 实例数，按实例数折算后填写" : undefined} onChange={(v) => set("tps", v)} />
          <TextField id="ch-window" label="窗口大小" required type="number" mono value={draft.window_size} error={errors.window_size}
            hint={telegram ? "每个 http-connector 实例上并行发送的请求数" : undefined} onChange={(v) => set("window_size", v)} />
          <TextField id="ch-retries" label="最大重试次数" required type="number" mono value={draft.max_submit_retries} error={errors.max_submit_retries} onChange={(v) => set("max_submit_retries", v)} />
          <TextField id="ch-backoff" label="重试退避（毫秒）" required mono value={draft.retry_backoff_ms} error={errors.retry_backoff_ms} hint="逗号分隔，按次序取用" onChange={(v) => set("retry_backoff_ms", v)} />
        </Form>
      </Fieldset>
      <Fieldset title="能力">
        <Form columns={2}>
          <Field label="能力标记">
            <div className="ax-stack">
              {CAPABILITIES.map((c) => {
                const locked = telegram && c.key === "dlr_supported";
                return <Checkbox key={c.key} label={c.label} description={telegram ? TELEGRAM_CAPABILITY_NOTE[c.key] : undefined}
                  checked={locked || draft[c.key]} disabled={locked} onChange={(on) => set(c.key, on)} />;
              })}
            </div>
          </Field>
          <Form>
            <TextField id="ch-max-length" label="最大长度" required type="number" mono value={draft.max_length} error={errors.max_length}
              hint={telegram ? TELEGRAM_MAX_LENGTH_NOTE : undefined} onChange={(v) => set("max_length", v)} />
          </Form>
        </Form>
      </Fieldset>
      <Fieldset title="Sender">
        <LinesField id="ch-senders" label="支持的 sender" value={draft.senders} hint="每行一个；留空表示不限。不在列表里的 sender 路由时剔除这条通道" rows={3} onChange={(v) => set("senders", v)} />
      </Fieldset>
    </div>
  );
}

/** SMPP 协议参数，字段同 internal/smpp 的 ChannelParams（决策 D28）。 */
function SMPPFields({ value, errors, onChange }: { value: SMPPDraft; errors: ChannelErrors; onChange: (v: SMPPDraft) => void }) {
  const field = (k: keyof SMPPDraft) => (v: string) => onChange({ ...value, [k]: v });
  return (
    <Form columns={2}>
      <TextField id="ch-host" label="上游地址" required mono value={value.host} error={errors["smpp.host"]} onChange={field("host")} />
      <TextField id="ch-port" label="端口" required type="number" mono value={value.port} error={errors["smpp.port"]} onChange={field("port")} />
      <TextField id="ch-system-id" label="system_id" required mono value={value.system_id} error={errors["smpp.system_id"]} onChange={field("system_id")} />
      <TextField id="ch-password" label="密码" mono value={value.password} hint="显示为 **** 时保持不动即不修改" onChange={field("password")} />
      <TextField id="ch-system-type" label="system_type" mono value={value.system_type} hint="上游要求时才填" onChange={field("system_type")} />
      <TextField id="ch-address-range" label="address_range" mono value={value.address_range} hint="上游要求时才填" onChange={field("address_range")} />
      <TextField id="ch-enquire-link" label="心跳间隔" mono value={value.enquire_link_interval} error={errors["smpp.enquire_link_interval"]} hint="例如 30s；留空不主动发心跳" onChange={field("enquire_link_interval")} />
    </Form>
  );
}

/** 选项列表：第一项是缺省，值为空串，不写进 config。 */
function withDefault(fallback: string, options: readonly (readonly [string, string])[]) {
  return [{ value: "", label: fallback }, ...options.map(([value, label]) => ({ value, label }))];
}

/** SMPP 对接兼容参数（决策 D151）：全部缺省时与原来的发送方式逐字节一致，只在上游有要求时修改。 */
function CompatFields({ value, errors, onChange }: { value: CompatDraft; errors: ChannelErrors; onChange: (v: CompatDraft) => void }) {
  const pick = (k: Exclude<keyof CompatDraft, "force_ucs2">, label: string, options: { value: string; label: string }[], help?: string) => (
    <Field label={label} help={help} error={errors[`compat.${k}`]}>
      <Select block ariaLabel={label} options={options} value={value[k]} onChange={(v) => onChange({ ...value, [k]: v })} />
    </Field>
  );
  return (
    <div className="ax-stack">
      <Form columns={2}>
        {pick("dest_ton", "目的地址 TON", withDefault("缺省（1 国际）", TON_OPTIONS))}
        {pick("dest_npi", "目的地址 NPI", withDefault("缺省（1 ISDN）", NPI_OPTIONS))}
        {pick("source_ton", "源地址 TON", withDefault("自动", TON_OPTIONS), "自动：SMPP 客户沿用提交的值，HTTP 按 sender 推断（纯数字 1，其余 5）。设置后对所有 sender 一律使用这个值")}
        {pick("source_npi", "源地址 NPI", withDefault("自动", NPI_OPTIONS), "自动：SMPP 客户沿用提交的值，HTTP 按 sender 推断（纯数字 1，其余 0）。设置后对所有 sender 一律使用这个值")}
        {pick("dest_format", "目的号码格式", withDefault("缺省（不带 +）", [["plus", "带 +"], ["00", "00 前缀"], ["national", "国内格式"]]),
          "缺省 6281234567890；带 + 为 +6281234567890；00 前缀为 006281234567890；国内格式为 81234567890。国内格式只能发往国家码对应的国家，别的国家按永久失败；通常还要把目的地址 TON 改成 2")}
        {value.dest_format === "national" && (
          <TextField id="ch-country-code" label="国家码" required mono value={value.country_code} error={errors["compat.country_code"]} hint="例如 62" onChange={(v) => onChange({ ...value, country_code: v })} />
        )}
        {pick("concat", "长短信方式", withDefault("缺省（UDH 8 位）", [["udh16", "UDH 16 位"], ["sar", "SAR"], ["payload", "message_payload"]]),
          "UDH 16 位每段少一个字符，可能多一段；message_payload 整条只发一个 PDU、只有一个上游 ID，成本仍按 UDH 8 位的段数计。客户计费不变")}
        {pick("gsm7_dcs", "GSM7 的 data_coding", withDefault("缺省（0）", [["1", "1（ASCII）"]]), "取 1 时纯 ASCII 正文按 ASCII 字节发，含其他字符的仍按 0")}
        {pick("latin1", "Latin-1 内容", withDefault("缺省（DCS 3）", [["ucs2", "转 UCS-2"]]), "上游不支持 DCS 3 时选；需要通道支持 Unicode")}
      </Form>
      <Field label="一律 UCS-2" error={errors["compat.force_ucs2"]}>
        <Checkbox label="所有文本按 UCS-2 发送" description="需要通道支持 Unicode；通道成本可能增加，客户计费不变" checked={value.force_ucs2} onChange={(on) => onChange({ ...value, force_ucs2: on })} />
      </Field>
    </div>
  );
}

/** 连接上游的 TLS（决策 D51）：证书与私钥是 connector 主机上的文件路径。 */
function TLSFields({ value, errors, onChange }: { value: TLSDraft; errors: ChannelErrors; onChange: (v: TLSDraft) => void }) {
  const field = (k: "ca" | "client_cert" | "client_key" | "server_name") => (v: string) => onChange({ ...value, [k]: v });
  return (
    <div className="ax-stack">
      <Switch label="用 TLS 连接上游" checked={value.enabled} onChange={(on) => onChange({ ...value, enabled: on })} />
      {value.enabled && (
        <Form columns={2}>
          <Field label="证书校验"><Checkbox label="校验上游证书" description="关掉会写进审计，只在上游证书有问题且确认风险时关" checked={value.verify} onChange={(on) => onChange({ ...value, verify: on })} /></Field>
          <TextField id="ch-tls-server-name" label="校验用主机名" mono value={value.server_name} hint="留空取上游地址" onChange={field("server_name")} />
          <TextField id="ch-tls-ca" label="CA 证书" mono value={value.ca} hint="connector 主机上的文件路径；留空用系统根证书" onChange={field("ca")} />
          <TextField id="ch-tls-client-cert" label="客户端证书" mono value={value.client_cert} hint="文件路径，与私钥同时填写" onChange={field("client_cert")} />
          <TextField id="ch-tls-client-key" label="客户端私钥" mono value={value.client_key} error={errors["tls.client_key"]} hint="文件路径" onChange={field("client_key")} />
        </Form>
      )}
    </div>
  );
}
